<template>
  <div
    class="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-dark-900 dark:via-dark-900 dark:to-dark-800 transition-colors duration-300 relative overflow-hidden">
    
    <!-- Decorative Background Elements -->
    <div class="absolute inset-0 overflow-hidden pointer-events-none">
      <div class="absolute -top-40 -right-40 w-80 h-80 bg-blue-400/10 dark:bg-blue-500/5 rounded-full blur-3xl"></div>
      <div class="absolute top-1/3 -left-20 w-60 h-60 bg-indigo-400/10 dark:bg-indigo-500/5 rounded-full blur-3xl"></div>
      <div class="absolute bottom-20 right-1/4 w-40 h-40 bg-purple-400/10 dark:bg-purple-500/5 rounded-full blur-3xl"></div>
    </div>

    <!-- Header -->
    <header class="sticky top-0 z-40 bg-white dark:bg-dark-800 border-b border-gray-200 dark:border-dark-700 shadow-sm">
      <div class="container mx-auto px-4 sm:px-6 py-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-4">
            <div class="relative group">
              <div
                class="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 shadow-lg shadow-blue-500/30 flex items-center justify-center text-white text-2xl transform group-hover:scale-105 transition-all duration-300">
                <i class="fas fa-chart-line"></i>
              </div>
            </div>
            <div>
              <p class="text-[11px] uppercase tracking-wider font-bold text-blue-500 dark:text-blue-400 mb-0.5">
                Sales Operations</p>
              <h1
                class="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-900 to-indigo-900 dark:from-blue-100 dark:to-indigo-100">
                Sales Dashboard
              </h1>
              <div class="flex items-center gap-2 text-xs font-medium text-gray-500 dark:text-dark-400 mt-1">
                <span class="relative flex h-2 w-2">
                  <span v-if="consolidatedRealtime.isConnected.value"
                    class="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span class="relative inline-flex rounded-full h-2 w-2"
                    :class="consolidatedRealtime.isConnected.value ? 'bg-emerald-500' : 'bg-amber-500'"></span>
                </span>
                <span>{{ consolidatedRealtime.isConnected.value ? 'Live Sync Active' : 'Connecting...' }}</span>
              </div>
            </div>
          </div>

          <button @click="refreshDashboard"
            class="p-2 rounded-xl text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/30 transition-colors"
            :disabled="loading" title="Refresh Dashboard">
            <i class="fas fa-sync-alt" :class="{ 'animate-spin': loading }"></i>
          </button>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <main class="relative z-10 container mx-auto px-4 sm:px-6 py-8">

      <!-- Loading State -->
      <Transition enter-active-class="transition duration-300" enter-from-class="opacity-0 scale-95" enter-to-class="opacity-100 scale-100">
        <div v-if="loading && !followUps.length && !quotationFollowUps.length" class="flex flex-col items-center justify-center py-32">
          <div class="relative">
            <div class="w-20 h-20 border-4 border-blue-200 dark:border-blue-800 border-t-blue-600 dark:border-t-blue-400 rounded-full animate-spin"></div>
            <div class="absolute inset-0 flex items-center justify-center">
              <div class="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white">
                <i class="fas fa-chart-line text-xl animate-pulse"></i>
              </div>
            </div>
          </div>
          <p class="mt-6 text-lg font-semibold text-gray-700 dark:text-gray-300 animate-pulse">Loading your dashboard...</p>
          <p class="text-sm text-gray-500 dark:text-gray-400">Please wait while we fetch your data</p>
        </div>
      </Transition>

      <!-- Error Message -->
      <Transition enter-active-class="transition duration-300" enter-from-class="opacity-0 -translate-y-4" enter-to-class="opacity-100 translate-y-0">
        <div v-if="error"
          class="mb-6 bg-white dark:bg-dark-800 rounded-2xl border-l-4 border-red-500 p-6 bg-red-50/50 dark:bg-red-900/20 shadow-sm border border-red-200 dark:border-red-800">
          <div class="flex items-start gap-4">
            <div class="flex-shrink-0">
              <div class="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/50 flex items-center justify-center">
                <i class="fas fa-exclamation-circle text-red-500 dark:text-red-400 text-xl"></i>
              </div>
            </div>
            <div class="flex-1">
              <h3 class="text-lg font-bold text-red-800 dark:text-red-300 mb-1">Error Loading Dashboard</h3>
              <p class="text-sm text-red-700 dark:text-red-300">
                {{ error.message }}
              </p>
              <button v-if="error.status >= 500" @click="fetchDashboardData"
                class="mt-3 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white font-semibold text-sm shadow-md hover:shadow-lg transition-all">
                <i class="fas fa-redo mr-2"></i>Retry
              </button>
            </div>
          </div>
        </div>
      </Transition>

      <!-- Dashboard Grid - Smart Layout with Responsive Reflow -->
      <div v-if="!loading || followUps.length || quotationFollowUps.length" 
        class="grid grid-cols-1 gap-6 transition-all duration-500"
        :class="smartGridClass">
        
        <!-- Main Content Area (Left) - Dynamic Width -->
        <div class="space-y-6 min-h-[500px]">
          <!-- Department Overview Card -->
          <div class="bg-white dark:bg-dark-800 rounded-2xl border border-gray-200 dark:border-dark-700 shadow-sm overflow-hidden">
            <div class="p-6 bg-gradient-to-r from-blue-50/50 to-indigo-50/50 dark:from-blue-900/10 dark:to-indigo-900/10 border-b border-gray-200/50 dark:border-dark-700/50">
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-3">
                  <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white shadow-lg">
                    <i class="fas fa-building"></i>
                  </div>
                  <div>
                    <h2 class="text-xl font-bold text-gray-900 dark:text-white">Department Performance</h2>
                    <p class="text-sm text-gray-600 dark:text-gray-400">Real-time analytics and insights</p>
                  </div>
                </div>
                <!-- Layout Controls (Desktop Only) -->
                <div class="hidden lg:flex items-center gap-2">
                  <!-- Smart Defaults Indicator -->
                  <button v-if="hasUserOverride && hasSidebarContent" @click="resetToSmartDefaults"
                    class="flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
                    title="Reset to smart collapse defaults">
                    <i class="fas fa-magic text-[10px]"></i>
                    Auto
                  </button>
                  
                  <!-- Layout Toggle -->
                  <button v-if="hasSidebarContent" @click="toggleLayoutMode" 
                    class="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold bg-white dark:bg-dark-700 border border-gray-200 dark:border-dark-600 hover:bg-gray-50 dark:hover:bg-dark-600 transition-colors">
                    <i class="fas" :class="isFullWidthLayout ? 'fa-columns' : 'fa-expand'"></i>
                    {{ isFullWidthLayout ? 'Split View' : 'Full Width' }}
                  </button>
                </div>
              </div>
            </div>
            <div class="p-6">
              <DepartmentOverview ref="departmentOverviewRef" :initial-date="departmentFilterDate"
                :initial-department="selectedDepartment" @update:date="departmentFilterDate = $event"
                @update:department="selectedDepartment = $event" 
                @update:rowCount="mainContentRowCount = $event"
                @open-order="openOrderPreviewModal"
                @open-payment="openPaymentPreviewModal" @open-expense="openExpensePreviewModal" />
            </div>
          </div>
        </div>

        <!-- Sidebar (Right) - Collapsible Sections -->
        <aside class="space-y-6" :class="{ 'lg:order-first': isMobileSidebarFirst }">
          <!-- Payment Follow-ups Widget -->
          <div class="bg-white dark:bg-dark-800 rounded-2xl border border-gray-200 dark:border-dark-700 shadow-sm overflow-hidden hover:shadow-md transition-all duration-300">
            <div class="p-4 bg-gradient-to-r from-amber-50/50 to-orange-50/50 dark:from-amber-900/10 dark:to-orange-900/10 border-b border-gray-200/50 dark:border-dark-700/50 cursor-pointer"
              @click="toggleSection('followups')">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white shadow-lg">
                  <i class="fas fa-exclamation-circle"></i>
                </div>
                <div class="flex-1">
                  <h3 class="text-lg font-bold text-gray-900 dark:text-white">Payment Follow-ups</h3>
                  <p class="text-xs text-gray-600 dark:text-gray-400">{{ followUps.length }} pending</p>
                </div>
                <button class="w-8 h-8 rounded-lg hover:bg-white/50 dark:hover:bg-dark-700/50 flex items-center justify-center transition-all">
                  <i class="fas fa-chevron-down text-gray-500 transition-transform duration-300" 
                    :class="{ 'rotate-180': !collapsedSections.followups }"></i>
                </button>
              </div>
            </div>
            <Transition
              enter-active-class="transition-all duration-300 ease-out"
              enter-from-class="max-h-0 opacity-0"
              enter-to-class="max-h-[400px] opacity-100"
              leave-active-class="transition-all duration-300 ease-in"
              leave-from-class="max-h-[400px] opacity-100"
              leave-to-class="max-h-0 opacity-0">
              <div v-show="!collapsedSections.followups" class="max-h-[400px] overflow-y-auto custom-scrollbar">
                <FollowupSection :follow-ups="followUps" :default-sort="followupFilters.sort"
                  @open-order="openOrderPreviewModal" @view-all="openPaymentFollowUpsModal" />
              </div>
            </Transition>
          </div>

          <!-- Quotations Widget -->
          <div class="bg-white dark:bg-dark-800 rounded-2xl border border-gray-200 dark:border-dark-700 shadow-sm overflow-hidden hover:shadow-md transition-all duration-300">
            <div class="p-4 bg-gradient-to-r from-blue-50/50 to-cyan-50/50 dark:from-blue-900/10 dark:to-cyan-900/10 border-b border-gray-200/50 dark:border-dark-700/50 cursor-pointer"
              @click="toggleSection('quotations')">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-400 to-cyan-500 flex items-center justify-center text-white shadow-lg">
                  <i class="fas fa-file-invoice"></i>
                </div>
                <div class="flex-1">
                  <h3 class="text-lg font-bold text-gray-900 dark:text-white">Quotation Follow-ups</h3>
                  <p class="text-xs text-gray-600 dark:text-gray-400">{{ quotationFollowUps.length }} to follow up</p>
                </div>
                <button class="w-8 h-8 rounded-lg hover:bg-white/50 dark:hover:bg-dark-700/50 flex items-center justify-center transition-all">
                  <i class="fas fa-chevron-down text-gray-500 transition-transform duration-300" 
                    :class="{ 'rotate-180': !collapsedSections.quotations }"></i>
                </button>
              </div>
            </div>
            <Transition
              enter-active-class="transition-all duration-300 ease-out"
              enter-from-class="max-h-0 opacity-0"
              enter-to-class="max-h-[400px] opacity-100"
              leave-active-class="transition-all duration-300 ease-in"
              leave-from-class="max-h-[400px] opacity-100"
              leave-to-class="max-h-0 opacity-0">
              <div v-show="!collapsedSections.quotations" class="max-h-[400px] overflow-y-auto custom-scrollbar">
                <QuotationSection :quotations="quotationFollowUps" :default-sort="quotationFilters.sort"
                  @view-quotation="viewQuotationDetails" />
              </div>
            </Transition>
          </div>

          <!-- Client Payments Widget -->
          <div class="bg-white dark:bg-dark-800 rounded-2xl border border-gray-200 dark:border-dark-700 shadow-sm overflow-hidden hover:shadow-md transition-all duration-300">
            <div class="p-4 bg-gradient-to-r from-green-50/50 to-emerald-50/50 dark:from-green-900/10 dark:to-emerald-900/10 border-b border-gray-200/50 dark:border-dark-700/50 cursor-pointer"
              @click="toggleSection('clients')">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center text-white shadow-lg">
                  <i class="fas fa-users"></i>
                </div>
                <div class="flex-1">
                  <h3 class="text-lg font-bold text-gray-900 dark:text-white">Client Accounts</h3>
                  <p class="text-xs text-gray-600 dark:text-gray-400">{{ clientPayments.length }} accounts</p>
                </div>
                <button class="w-8 h-8 rounded-lg hover:bg-white/50 dark:hover:bg-dark-700/50 flex items-center justify-center transition-all">
                  <i class="fas fa-chevron-down text-gray-500 transition-transform duration-300" 
                    :class="{ 'rotate-180': !collapsedSections.clients }"></i>
                </button>
              </div>
            </div>
            <Transition
              enter-active-class="transition-all duration-300 ease-out"
              enter-from-class="max-h-0 opacity-0"
              enter-to-class="max-h-[400px] opacity-100"
              leave-active-class="transition-all duration-300 ease-in"
              leave-from-class="max-h-[400px] opacity-100"
              leave-to-class="max-h-0 opacity-0">
              <div v-show="!collapsedSections.clients" class="max-h-[400px] overflow-y-auto custom-scrollbar">
                <ClientPaymentSection :clients="clientPayments" @view-client-orders="viewClientDetails"
                  @view-all="openClientPaymentStatusModal" />
              </div>
            </Transition>
          </div>
        </aside>
      </div>

    </main>

    <!-- Compact FAB Menu -->
    <div class="fixed bottom-6 right-6 z-50">
      <!-- Action Menu -->
      <Transition
        enter-active-class="transition duration-150 ease-out"
        enter-from-class="transform scale-95 opacity-0"
        enter-to-class="transform scale-100 opacity-100"
        leave-active-class="transition duration-100 ease-in"
        leave-from-class="transform scale-100 opacity-100"
        leave-to-class="transform scale-95 opacity-0">
        <div v-if="showFloatingTools" class="mb-2 bg-white dark:bg-dark-800 rounded-xl shadow-lg border border-gray-200 dark:border-dark-700 p-2">
          <button v-for="(action, index) in [
            { icon: 'fa-shopping-cart', label: 'Order', action: createOrder },
            { icon: 'fa-user-plus', label: 'Client', action: createClient },
            { icon: 'fa-money-bill-wave', label: 'Payment', action: addPayment },
            { icon: 'fa-receipt', label: 'Expense', action: createExpense },
            { icon: 'fa-file-invoice', label: 'Quote', action: createQuotation },
            { icon: 'fa-chart-bar', label: 'Report', action: generateReport, disabled: checkingDailyLimit },
            { icon: 'fa-tools', label: 'Tools', action: createTools }
          ]" :key="index"
            @click="action.action(); showFloatingTools = false"
            :disabled="action.disabled"
            class="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed">
            <i class="fas text-gray-600 dark:text-gray-400 w-4 text-center" :class="action.disabled ? 'fa-spinner fa-spin' : action.icon"></i>
            <span class="text-sm font-medium text-gray-900 dark:text-white">{{ action.label }}</span>
          </button>
        </div>
      </Transition>

      <!-- Main FAB -->
      <button @click="showFloatingTools = !showFloatingTools"
        class="w-12 h-12 rounded-full bg-teal-600 hover:bg-teal-700 dark:bg-teal-500 dark:hover:bg-teal-600 shadow-lg hover:shadow-xl flex items-center justify-center text-white transition-all duration-200"
        :class="{ 'rotate-45': showFloatingTools }">
        <i class="fas fa-plus text-lg transition-transform duration-200"></i>
      </button>
    </div>

    <!-- Global FAB Modals are handled by DefaultLayout.vue -->

    <!-- Quotation Preview Modal -->
    <QuotationPreviewModal v-if="selectedQuotationId" v-model="showQuotationPreviewModal"
      :quotation-id="selectedQuotationId" :hide-pdf-button="true"
      @update:modelValue="(val) => { if (!val) selectedQuotationId = null; }" class="quotation-modal" />


    <!-- Shift Report Modal -->
    <Modal v-model="showShiftReportModal" size="4xl" :prevent-outside-close="true">
      <template #title>
        <div class="flex items-center gap-2">
          <i class="fas fa-file-alt text-purple-600"></i>
          <span>Generate Shift Report</span>
        </div>
      </template>

      <template #content>
        <div class="p-3 sm:p-4">
          <!-- Report Form -->
          <form @submit.prevent="generateShiftReportPDF" class="space-y-3">
            <!-- Basic Information - Compact Horizontal Layout -->
            <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
              <!-- Date Field -->
              <div>
                <label class="block text-xs font-medium text-gray-700 dark:text-dark-300 mb-1">Date</label>
                <input type="date" v-model="shiftReportData.date" :disabled="!hasRole(['super_admin'])"
                  class="w-full rounded-md border-gray-300 dark:border-dark-600 bg-white dark:bg-dark-800 text-gray-900 dark:text-dark-100 shadow-sm focus:border-blue-500 focus:ring-blue-500 transition-colors px-2 py-1.5 text-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100 dark:disabled:bg-dark-700"
                  required />
              </div>

              <!-- Petty Cash Field -->
              <div>
                <label class="block text-xs font-medium text-gray-700 dark:text-dark-300 mb-1">Petty Cash</label>
                <div class="relative">
                  <span
                    class="absolute inset-y-0 left-0 pl-2 flex items-center text-gray-500 dark:text-dark-400 text-sm">₱</span>
                  <input type="number" v-model="shiftReportData.pettyCash"
                    class="w-full pl-7 rounded-md border-gray-300 dark:border-dark-600 bg-white dark:bg-dark-800 text-gray-900 dark:text-dark-100 shadow-sm focus:border-blue-500 focus:ring-blue-500 transition-colors px-2 py-1.5 text-sm"
                    step="0.01" min="0" placeholder="0.00" />
                </div>
              </div>

              <!-- Remaining Cash Sales Field -->
              <div>
                <label class="block text-xs font-medium text-gray-700 dark:text-dark-300 mb-1">Remaining Cash
                  Sales</label>
                <div class="relative">
                  <span
                    class="absolute inset-y-0 left-0 pl-2 flex items-center text-gray-500 dark:text-dark-400 text-sm">₱</span>
                  <input type="number" v-model="shiftReportData.remainingCashSales"
                    class="w-full pl-7 rounded-md border-gray-300 dark:border-dark-600 bg-white dark:bg-dark-800 text-gray-900 dark:text-dark-100 shadow-sm focus:border-blue-500 focus:ring-blue-500 transition-colors px-2 py-1.5 text-sm"
                    step="0.01" min="0" placeholder="0.00" />
                </div>
              </div>
            </div>

            <!-- Cash Count Section - Compact -->
            <div class="bg-blue-50 dark:bg-blue-900/10 rounded-md p-3 border border-blue-200 dark:border-blue-800">
              <div class="flex items-center justify-between mb-2">
                <h3 class="text-xs font-semibold text-gray-900 dark:text-dark-100 flex items-center">
                  <i class="fas fa-money-bill-wave text-blue-600 mr-1.5 text-xs"></i>
                  Physical Cash Count
                </h3>
                <!-- Entered Total Display - Inline -->
                <div
                  class="flex items-center gap-2 bg-white dark:bg-dark-800 px-3 py-1.5 rounded-md border border-blue-300 dark:border-blue-700">
                  <span class="text-xs font-medium text-gray-700 dark:text-dark-300">Total:</span>
                  <span class="text-base font-bold text-blue-600 dark:text-blue-400">
                    ₱{{ enteredTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
                  </span>
                </div>
              </div>

              <!-- Denomination Grid - 2 columns for compactness -->
              <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                <div v-for="denom in denominations" :key="denom.value"
                  class="bg-white dark:bg-dark-800 rounded-md border border-gray-200 dark:border-dark-600 p-2">
                  <div class="flex items-center justify-between mb-1">
                    <span class="text-xs font-medium text-gray-700 dark:text-dark-300">P{{ denom.value }}</span>
                    <span class="text-xs text-gray-500 dark:text-dark-400 font-mono">
                      ₱{{ ((denom.value * (shiftReportData.denominations[denom.value] ||
                        0)).toFixed(2)).replace(/\B(?=(\d{3})+(?!\d))/g, ',') }}
                    </span>
                  </div>
                  <input type="number" v-model.number="shiftReportData.denominations[denom.value]"
                    class="w-full rounded-md border-gray-300 dark:border-dark-600 bg-white dark:bg-dark-800 text-gray-900 dark:text-dark-100 shadow-sm focus:border-blue-500 focus:ring-blue-500 transition-colors px-2 py-1 text-xs"
                    min="0" step="1" placeholder="0" @input="calculateEnteredTotal" />
                </div>
              </div>
              <p class="text-xs text-gray-500 dark:text-dark-400 mt-2 italic">
                <i class="fas fa-info-circle mr-1 text-xs"></i>
                Expected amount will be shown after submission
              </p>
            </div>

            <!-- Notes Field - Compact -->
            <div>
              <label class="block text-xs font-medium text-gray-700 dark:text-dark-300 mb-1">
                <i class="fas fa-sticky-note text-gray-500 mr-1 text-xs"></i>
                Notes (Optional)
              </label>
              <textarea v-model="shiftReportData.notes" rows="2"
                class="w-full rounded-md border-gray-300 dark:border-dark-600 bg-white dark:bg-dark-800 text-gray-900 dark:text-dark-100 shadow-sm focus:border-blue-500 focus:ring-blue-500 transition-colors px-2 py-1.5 text-sm"
                placeholder="Enter any additional notes here..."></textarea>
            </div>

            <!-- Submit Buttons - Compact -->
            <div class="flex flex-col sm:flex-row justify-end gap-2 pt-2 border-t border-gray-200 dark:border-dark-600">
              <button type="button" @click="showShiftReportModal = false"
                class="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 dark:text-dark-300 bg-white dark:bg-dark-800 border border-gray-300 dark:border-dark-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-500 transition-colors">
                <i class="fas fa-times mr-1.5"></i>
                Cancel
              </button>

              <button type="submit"
                class="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                :disabled="shiftReportLoading">
                <span v-if="shiftReportLoading">
                  <i class="fas fa-spinner fa-spin mr-1.5"></i>
                  Generating...
                </span>
                <span v-else>
                  <i class="fas fa-file-pdf mr-1.5"></i>
                  Generate Report
                </span>
              </button>
            </div>
          </form>
        </div>
      </template>
    </Modal>



    <!-- Order Preview Modal -->
    <Modal v-if="showOrderPreviewModal" v-model="showOrderPreviewModal" size="5xl">
      <template #title>
        <div class="flex items-center gap-2">
          <i class="fas fa-shopping-cart text-blue-600"></i>
          <span>Order Details</span>
        </div>
      </template>
      <template #content>
        <OrderPreview v-if="selectedOrderId" :orderId="selectedOrderId" />
      </template>
    </Modal>

    <!-- Payment Preview Modal -->
    <Modal v-if="showPaymentPreviewModal" v-model="showPaymentPreviewModal" size="3xl">
      <template #title>
        <div class="flex items-center gap-2">
          <i class="fas fa-money-bill-wave text-blue-600"></i>
          <span>Payment Details</span>
        </div>
      </template>
      <template #content>
        <PaymentPreview v-if="selectedPaymentId" :paymentId="selectedPaymentId" />
      </template>
    </Modal>

    <!-- Expense Preview Modal -->
    <Modal v-if="showExpensePreviewModal" v-model="showExpensePreviewModal" size="5xl">
      <template #title>
        <div class="flex items-center gap-2">
          <i class="fas fa-receipt text-red-600"></i>
          <span>Expense Details</span>
        </div>
      </template>
      <template #content>
        <ExpensePreview v-if="showExpensePreviewModal && selectedExpense" v-model="showExpensePreviewModal"
          :expense="selectedExpense" />
      </template>
    </Modal>

    <!-- Payment Follow-ups Modal -->
    <Modal v-if="showPaymentFollowUpsModal" v-model="showPaymentFollowUpsModal" size="7xl">
      <template #title>
        <div class="flex items-center gap-2">
          <i class="fas fa-money-bill-wave text-blue-600"></i>
          <span>Payment Follow-ups</span>
        </div>
      </template>
      <template #content>
        <PaymentFollowUpsModal @view-order="viewFollowupOrder" @record-payment="navigateToRecordPayment" />
      </template>
    </Modal>

    <!-- Client Payment Status Modal -->
    <Modal v-if="showClientPaymentStatusModal" v-model="showClientPaymentStatusModal" size="7xl">
      <template #title>
        <div class="flex items-center gap-2">
          <i class="fas fa-wallet text-blue-600"></i>
          <span>Client Payment Status</span>
        </div>
      </template>
      <template #content>
        <ClientPaymentStatusModal @view-client="viewClientDetails" />
      </template>
    </Modal>

    <!-- Client Orders Modal -->
    <Modal v-if="showClientOrdersModal" v-model="showClientOrdersModal" size="7xl">
      <template #title>
        <div class="flex items-center gap-2">
          <i class="fas fa-shopping-cart text-blue-600"></i>
          <span>Client Orders - {{ selectedClientForOrders?.name }}</span>
        </div>
      </template>
      <template #content>
        <ClientOrdersModal v-if="selectedClientForOrders" :client="selectedClientForOrders"
          @view-order="viewOrderFromClientModal" />
      </template>
    </Modal>


  </div>
</template>
<script setup>
import { ref, onMounted, onBeforeUnmount, watch, computed } from 'vue';

import { useNotificationsStore } from '@/stores/notifications';
import { useToast } from '@/composables/useToast';
import { useApi } from '@/composables/useApi';
import { useRouter } from 'vue-router';
import { useAuth } from '@/composables/useAuth';
import { formatCurrency } from '@/utils/formatters';
import { useDashboardUtils } from '@/composables/useDashboardUtils';
import { useConsolidatedRealtime } from '@/composables/useConsolidatedRealtime';
import { useMasterData } from '@/composables/useMasterData';

// Import components
import DepartmentOverview from '@/components/dashboard/DepartmentOverview.vue';
import FollowupSection from '@/components/dashboard/sidebar/FollowupSection.vue';
import QuotationSection from '@/components/dashboard/sidebar/QuotationSection.vue';
import ClientPaymentSection from '@/components/dashboard/sidebar/ClientPaymentSection.vue';
import ProjectsChart from '@/components/dashboard/ProjectsChart.vue';
import QuotationPreviewModal from '@/components/clients/QuotationPreviewModal.vue';
import Modal from '@/components/common/Modal.vue';
import OrderPreview from '@/components/orders/OrderPreview.vue';
import PaymentPreview from '@/components/payments/PaymentPreview.vue';
import ExpensePreview from '@/components/expenses/ExpensePreview.vue';
import PaymentFollowUpsModal from '@/components/payments/PaymentFollowUpsModal.vue';
import ClientPaymentStatusModal from '@/components/dashboard/ClientPaymentStatusModal.vue';
import ClientOrdersModal from '@/components/dashboard/ClientOrdersModal.vue';

// Import utilities
import { useToast as useToastVueToastification } from 'vue-toastification';

// Initialize composables
const api = useApi();
const router = useRouter();
const toast = useToast();
const masterData = useMasterData();
const toastVueToastification = useToastVueToastification();
const { user, hasPermission, hasRole } = useAuth();
const notificationsStore = useNotificationsStore();
const dashboardUtils = useDashboardUtils();
const consolidatedRealtime = useConsolidatedRealtime();

const lastUpdatedTime = ref(new Date().toLocaleString());

// Update the last updated time
const updateLastUpdatedTime = () => {
  lastUpdatedTime.value = new Date().toLocaleString();
};

// Initialize state
const loading = ref(false);
const error = ref(null);
const retryCount = ref(0);
const maxRetries = 3;

// CRITICAL: Adding department-related state declarations first to avoid initialization issues
const departmentFilterDate = ref(dashboardUtils.getCurrentFormattedDate());
console.log('[SalesDashboard] Department filter date initialized with:', {
  utilFormattedDate: dashboardUtils.getCurrentFormattedDate(),
  departmentFilterDate: departmentFilterDate.value
});
const selectedDepartment = ref('all');

// Add state for modals
const showQuotationPreviewModal = ref(false);
const selectedQuotationId = ref(null);
const showOrderPreviewModal = ref(false);
const selectedOrderId = ref(null);
const showPaymentPreviewModal = ref(false);
const selectedPaymentId = ref(null);
const showExpensePreviewModal = ref(false);
const selectedExpense = ref(null);
const showPaymentFollowUpsModal = ref(false);
const showClientPaymentStatusModal = ref(false);
const showClientOrdersModal = ref(false);
const selectedClientForOrders = ref(null);
const showFloatingTools = ref(false);

// Add ref for department overview component
const departmentOverviewRef = ref(null);

// Add the missing refs that were removed but are still used in the template
const followUps = ref([]);
const clientPayments = ref([]);

// Add new state for follow-up filters
const followupFilters = ref({
  sort: 'oldest',
  urgency: 'all',
  age: 'all'
});

// Add new state for quotation filters
const quotationFilters = ref({
  sort: 'expiry'
});

// ============================================
// Smart Layout & Collapsible Sections State
// ============================================

// Track if user has manually overridden collapse states
const hasUserOverride = ref(localStorage.getItem('sales_dashboard_user_override') === 'true');

// Mark that user has manually toggled something
const setUserOverride = () => {
  hasUserOverride.value = true;
  localStorage.setItem('sales_dashboard_user_override', 'true');
};

// Load saved collapsed states from localStorage (only if user has override)
const loadCollapsedStates = () => {
  // Only load saved states if user has manually overridden
  if (!hasUserOverride.value) {
    console.log('[SalesDashboard] No user override, using smart defaults');
    return { followups: true, quotations: true, clients: true };
  }
  
  try {
    const saved = localStorage.getItem('sales_dashboard_collapsed_sections');
    if (saved) {
      console.log('[SalesDashboard] Loading saved collapse states:', saved);
      return JSON.parse(saved);
    }
  } catch (error) {
    console.error('Error loading collapsed states:', error);
  }
  // Default: all collapsed
  return { followups: true, quotations: true, clients: true };
};

// Collapsible section states - Initialize with all collapsed (will be adjusted by smart defaults)
const collapsedSections = ref({ followups: true, quotations: true, clients: true });

// Save collapsed states to localStorage whenever they change
watch(collapsedSections, (newStates) => {
  try {
    localStorage.setItem('sales_dashboard_collapsed_sections', JSON.stringify(newStates));
  } catch (error) {
    console.error('Error saving collapsed states:', error);
  }
}, { deep: true });

// Toggle a specific section (user action - sets override flag)
const toggleSection = (sectionName) => {
  collapsedSections.value[sectionName] = !collapsedSections.value[sectionName];
  setUserOverride(); // User manually toggled, so mark as override
};

// Calculate estimated row count from main content data
const estimatedMainContentRows = computed(() => {
  // Get data from department overview ref if available
  // Estimate based on visible orders, payments, and expenses
  
  // For now, we'll use a simple heuristic based on the component ref
  // You can enhance this by exposing actual counts from DepartmentOverview
  
  // Rough estimate: Each order/payment/expense = 1 row
  // This will be more accurate once we have access to actual component data
  return 0; // Will be updated by the component
});

// Exposed ref for actual row count (can be set by DepartmentOverview component)
const mainContentRowCount = ref(0);

// Apply smart collapse defaults based on content density
const applySmartCollapseDefaults = (force = false) => {
  // Only apply if user hasn't manually overridden (unless forced)
  if (hasUserOverride.value && !force) {
    console.log('[SalesDashboard] User has override, skipping smart defaults');
    return;
  }

  const rowCount = mainContentRowCount.value;
  
  console.log('[SalesDashboard] Applying smart collapse defaults for row count:', rowCount);
  
  if (rowCount === 0) {
    // No data: Collapse ALL (nothing to show anyway)
    collapsedSections.value = {
      followups: true,
      quotations: true,
      clients: true
    };
    console.log('[SalesDashboard] No data (0 rows): Collapsing all sidebar sections');
  } else if (rowCount >= 1 && rowCount <= 5) {
    // Few rows (1-5): Prioritize Client Accounts OPEN, collapse others
    collapsedSections.value = {
      followups: true, // COLLAPSED
      quotations: true, // COLLAPSED
      clients: false // OPEN - prioritized
    };
    console.log('[SalesDashboard] Few rows (1-5): Prioritizing Client Accounts, collapsing Follow-ups & Quotations');
  } else {
    // Lots of rows (6+): Open ALL sections
    collapsedSections.value = {
      followups: false, // OPEN
      quotations: false, // OPEN  
      clients: false // OPEN
    };
    console.log('[SalesDashboard] Lots of rows (6+): Opening all sidebar sections');
  }
};

// Reset user override and reapply smart defaults
const resetToSmartDefaults = () => {
  hasUserOverride.value = false;
  localStorage.removeItem('sales_dashboard_user_override');
  applySmartCollapseDefaults(true); // Force apply
  toast.success('Smart collapse defaults restored');
};

// Watch for row count changes and apply smart defaults
watch(mainContentRowCount, (newCount) => {
  console.log('[SalesDashboard] Main content row count changed to:', newCount);
  applySmartCollapseDefaults();
}, { immediate: true }); // Run immediately on mount

// Calculate row count based on available data (fallback heuristic)
const calculateRowCount = () => {
  // This is a fallback calculation when component ref is not available
  // You can enhance this based on your actual data structure
  
  // For now, estimate based on data we have
  let count = 0;
  
  // Estimate rows (this is approximate)
  // You'll want to get the actual count from DepartmentOverview component
  
  return count;
};

// Layout mode state - Load from localStorage
const isFullWidthLayout = ref(localStorage.getItem('sales_dashboard_layout') === 'full');

// Toggle layout mode
const toggleLayoutMode = () => {
  isFullWidthLayout.value = !isFullWidthLayout.value;
  localStorage.setItem('sales_dashboard_layout', isFullWidthLayout.value ? 'full' : 'split');
};

// Mobile sidebar ordering
const isMobileSidebarFirst = ref(false);

// Detect if we have meaningful sidebar content
const hasSidebarContent = computed(() => {
  return followUps.value.length > 0 || quotationFollowUps.value.length > 0 || clientPayments.value.length > 0;
});

// Detect if main content is minimal/empty
const isMainContentMinimal = computed(() => {
  return mainContentRowCount.value <= 3;
});

// Smart grid class - Adapts based on content and user preference
const smartGridClass = computed(() => {
  // Mobile: Always single column
  // Desktop: Smart layout based on content and user preference
  
  if (isFullWidthLayout.value) {
    // Full width mode: Sidebars go below
    return 'lg:grid-cols-1';
  }
  
  if (!hasSidebarContent.value) {
    // No sidebar content: Full width automatically
    return 'lg:grid-cols-1';
  }
  
  // Default: 9-3 split (main content vs sidebar)
  return 'lg:grid-cols-12 [&>*:first-child]:lg:col-span-9 [&>*:last-child]:lg:col-span-3';
});

// Add these to the script section after the other state declarations
const clientFilters = ref({
  sort: 'amount_high',
  status: 'all',
  search: ''
});

// Computed property for total outstanding
const totalOutstanding = computed(() => {
  return clientPayments.value.reduce((total, client) => total + (client.outstanding || 0), 0);
});

// State for shift report modal
const showShiftReportModal = ref(false);
const shiftReportLoading = ref(false);
const checkingDailyLimit = ref(false);

const denominations = [
  { value: 1000, label: 'P1000' },
  { value: 500, label: 'P500' },
  { value: 200, label: 'P200' },
  { value: 100, label: 'P100' },
  { value: 50, label: 'P50' },
  { value: 20, label: 'P20' },
  { value: 10, label: 'P10' },
  { value: 5, label: 'P5' },
  { value: 1, label: 'P1' }
];

const shiftReportData = ref({
  date: dashboardUtils.getCurrentFormattedDate(),
  notes: '',
  pettyCash: 0,
  remainingCashSales: 0,
  denominations: {
    1000: 0,
    500: 0,
    200: 0,
    100: 0,
    50: 0,
    20: 0,
    10: 0,
    5: 0,
    1: 0
  }
});

// Computed property for entered total
const enteredTotal = computed(() => {
  let total = 0;
  denominations.forEach(denom => {
    const count = shiftReportData.value.denominations[denom.value] || 0;
    total += denom.value * count;
  });
  return total;
});

// Calculate entered total (can be called manually if needed)
const calculateEnteredTotal = () => {
  // Already computed, this is just for reactivity
  return enteredTotal.value;
};

const selectedPeriod = ref('all_time');
const quotationsLoading = ref(false);
const quotationFollowUps = ref([]);

// Fallback polling mechanism - reduced frequency to minimize database load
let fallbackPollingInterval = null;
const FALLBACK_POLLING_INTERVAL = 300000; // 5 minutes (reduced from 1 minute)

// Store unsubscribe function for cleanup
let unsubscribeRealtime = null;

onMounted(async () => {
  // Load saved collapse states if user has override
  if (hasUserOverride.value) {
    const savedStates = loadCollapsedStates();
    if (savedStates) {
      collapsedSections.value = savedStates;
      console.log('[SalesDashboard] Loaded saved collapse states:', savedStates);
    }
  }
  
  // Create consolidated realtime subscriptions - dramatically reduces database load
  const handleDashboardUpdate = (payload) => {
    console.log('[ConsolidatedRealtime] Dashboard update received:', payload);
    fetchDashboardData();
    fetchQuotationFollowUps();
    // Refresh departmental data
    if (departmentOverviewRef.value?.refresh) {
      departmentOverviewRef.value.refresh();
    }
  };

  const handleQuotationsUpdate = (payload) => {
    console.log('[ConsolidatedRealtime] Quotations update received:', payload);
    fetchQuotationFollowUps();
  };

  const handleExpensesUpdate = (payload) => {
    console.log('[ConsolidatedRealtime] Expenses update received:', payload);
    // Refresh departmental data only
    if (departmentOverviewRef.value?.refresh) {
      departmentOverviewRef.value.refresh();
    }
  };

  // Set up consolidated subscriptions - shares connections with other components
  unsubscribeRealtime = await consolidatedRealtime.subscribeBatch([
    { table: 'payments', callback: handleDashboardUpdate },
    { table: 'orders', callback: handleDashboardUpdate },
    { table: 'quotations', callback: handleQuotationsUpdate },
    { table: 'expenses', callback: handleExpensesUpdate }
  ], 'SalesDashboard');

  // Notifications store is initialized by App.vue

  // Set up fallback polling when realtime is disconnected
  const startFallbackPolling = () => {
    if (fallbackPollingInterval) return;

    console.log('[SalesDashboard] Starting fallback polling');
    fallbackPollingInterval = setInterval(() => {
      if (!consolidatedRealtime.isConnected.value && consolidatedRealtime.isTabVisible.value) {
        console.log('[SalesDashboard] Fallback polling - refreshing data');
        fetchDashboardData();
        fetchQuotationFollowUps();
        if (departmentOverviewRef.value?.refresh) {
          departmentOverviewRef.value.refresh();
        }
      }
    }, FALLBACK_POLLING_INTERVAL);
  };

  const stopFallbackPolling = () => {
    if (fallbackPollingInterval) {
      clearInterval(fallbackPollingInterval);
      fallbackPollingInterval = null;
      console.log('[SalesDashboard] Stopped fallback polling');
    }
  };

  // Watch connection state to enable/disable fallback polling
  watch(() => consolidatedRealtime.isConnected.value, (isConnected) => {
    if (isConnected) {
      stopFallbackPolling();
    } else {
      startFallbackPolling();
    }
  });

  // Initial data fetch
  loading.value = true;
  try {
    await Promise.all([
      fetchDashboardData(),
      fetchQuotationFollowUps()
    ]);
    loading.value = false;
    console.log('[SalesDashboard] Initial data fetch complete');

    // Start fallback polling if realtime is not connected
    if (!consolidatedRealtime.isConnected.value) {
      startFallbackPolling();
    }
  } catch (error) {
    console.error('[SalesDashboard] Error during initial data load:', error);
    loading.value = false;
    toast.error('Failed to load dashboard data');
  }
});

onBeforeUnmount(() => {
  // Stop fallback polling
  if (fallbackPollingInterval) {
    clearInterval(fallbackPollingInterval);
    fallbackPollingInterval = null;
  }

  // Cleanup consolidated realtime subscriptions
  if (unsubscribeRealtime) {
    unsubscribeRealtime();
    console.log('[SalesDashboard] Cleaned up consolidated realtime subscriptions');
  }
});

// Add refresh function for manual refresh button
const refreshDashboard = async () => {
  console.log('[SalesDashboard] Manual refresh triggered');
  await Promise.all([
    fetchDashboardData(),
    fetchQuotationFollowUps()
  ]);
  if (departmentOverviewRef.value?.refresh) {
    departmentOverviewRef.value.refresh();
  }
};

// Fetch dashboard data
const fetchDashboardData = async () => {
  try {
    loading.value = true;
    error.value = null;
    const response = await api.get('/dashboard/sales', {
      params: {
        period: selectedPeriod.value
      }
    });

    if (!response?.data) {
      throw new Error('Invalid response format - missing data');
    }
    const data = response.data;

    // Update follow-ups with proper null checks
    followUps.value = (data.followUps || []).map(followup => {
      const days_old = Math.floor((new Date() - new Date(followup.date_received)) / (1000 * 60 * 60 * 24));
      return {
        id: followup.order_id,
        clientName: followup.client_name,
        orderNumber: followup.order_number || 'N/A',
        amountDue: parseFloat(followup.amount_due) || 0,
        days_old: days_old,
        status: dashboardUtils.getFollowUpStatus(followup.date_received),
        dueDate: dashboardUtils.formatDate(followup.date_received),
        urgencyClass: dashboardUtils.getUrgencyClass(days_old),
        statusColor: dashboardUtils.getStatusColor(days_old)
      };
    });

    // Update client payments with proper null checks
    clientPayments.value = (data.clientPayments || []).map(client => {
      return {
        id: client.client_id,
        name: client.client_name,
        pendingOrders: parseInt(client.pending_orders) || 0,
        outstanding: parseFloat(client.outstanding_amount) || 0,
        lastPayment: dashboardUtils.formatDate(client.last_payment_date)
      };
    });
    retryCount.value = 0;
    updateLastUpdatedTime();
  } catch (error) {
    console.error('[SalesDashboard] Error:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status
    });

    error.value = {
      message: error.response?.data?.message || error.message || 'Failed to load dashboard data',
      code: error.response?.data?.code || error.code || 'UNKNOWN_ERROR',
      status: error.response?.status
    };
    // Implement retry logic for certain errors
    if (retryCount.value < maxRetries && error.response?.status >= 500) {
      retryCount.value++;
      console.log(`[SalesDashboard] Retrying (${retryCount.value}/${maxRetries})...`);
      setTimeout(() => {
        fetchDashboardData();
      }, 1000 * retryCount.value); // Exponential backoff
    }
  } finally {
    loading.value = false;
  }
};

// Add new function to fetch quotation follow-ups
const fetchQuotationFollowUps = async () => {
  try {
    quotationsLoading.value = true;

    // Add pagination parameters to get all quotations
    const response = await api.get('/dashboard/quotation-followups', {
      params: {
        limit: 5,
        page: 1,
        include_all: true
      }
    });

    if (response?.data) {
      quotationFollowUps.value = response.data;
    }
  } catch (error) {
    toast.error('Failed to load quotation follow-ups');
  } finally {
    quotationsLoading.value = false;
  }
};

// Update watchers
watch([selectedPeriod, followupFilters], () => {
  if (error.value) {
    error.value = null; // Clear previous errors when changing period
  }

  // Only fetch new data when period changes
  if (selectedPeriod.value) {
    fetchDashboardData();
    fetchQuotationFollowUps();
  }
}, { deep: true });

// Fetch departmental data (proxy function)
const fetchDepartmentData = async () => {
  try {
    console.log('[SalesDashboard] Department data fetch requested');
    // Call the departmental component's refresh method
    if (departmentOverviewRef.value?.refresh) {
      await departmentOverviewRef.value.refresh();
      console.log('[SalesDashboard] Department data refreshed successfully');
    } else {
      console.warn('[SalesDashboard] Department overview component not ready for refresh');
    }
  } catch (error) {
    console.error('[SalesDashboard] Error in department data fetch request:', error);
    toast.error('Failed to load department data');
  }
};

// Function to view quotation details
const viewQuotationDetails = (quotationId) => {
  if (!quotationId) {
    console.error('No quotation ID provided');
    return;
  }

  console.log('Opening quotation preview modal for ID:', quotationId);
  selectedQuotationId.value = quotationId;
  showQuotationPreviewModal.value = true;
};

// Record payment for client
// Navigation functions
const navigateToShiftReport = async () => {
  try {
    const currentDate = dashboardUtils.getCurrentFormattedDate();

    // Check if user has unlimited access (super_admin, admin, or manager)
    const hasUnlimitedAccess = hasRole(['super_admin', 'admin', 'manager']);

    // Only check daily limit if user doesn't have unlimited access
    if (!hasUnlimitedAccess) {
      checkingDailyLimit.value = true;

      // Check if report already generated for today
      const checkResponse = await api.get('/reports/shift-report/check', {
        params: { date: currentDate }
      });

      if (checkResponse.data?.exists) {
        toast.error(`Shift report already generated for ${currentDate}. Only one report per day is allowed. Contact manager if you need to regenerate.`);
        checkingDailyLimit.value = false;
        return;
      }

      checkingDailyLimit.value = false;
    }

    // Reset form data with current date
    shiftReportData.value = {
      date: currentDate,
      notes: '',
      pettyCash: 0,
      remainingCashSales: 0,
      denominations: {
        1000: 0,
        500: 0,
        200: 0,
        100: 0,
        50: 0,
        20: 0,
        10: 0,
        5: 0,
        1: 0
      }
    };

    showShiftReportModal.value = true;
    console.log('Shift report modal opened with date:', currentDate, 'Unlimited access:', hasUnlimitedAccess);
  } catch (error) {
    console.error('Error checking daily limit:', error);
    checkingDailyLimit.value = false;
    toast.error('Failed to check shift report status. Please try again.');
  }
};


// Shift report functions


const generateShiftReportPDF = async () => {
  try {
    shiftReportLoading.value = true;

    // Validate that at least some denominations are entered
    const hasAnyDenomination = Object.values(shiftReportData.value.denominations).some(count => count > 0);
    if (!hasAnyDenomination) {
      toast.error('Please enter cash counts for at least one denomination before generating the report.');
      shiftReportLoading.value = false;
      return;
    }

    // Safely construct staffName with correct property names (camelCase)
    const staffName = user.value && user.value.firstName && user.value.lastName
      ? `${user.value.firstName} ${user.value.lastName}`
      : 'N/A';

    // Parse numeric values properly for PDF generation
    const pettyCash = parseFloat(shiftReportData.value.pettyCash) || 0;
    const remainingCashSales = parseFloat(shiftReportData.value.remainingCashSales) || 0;

    console.log('Generating shift report PDF with data:', {
      date: shiftReportData.value.date,
      staffName,
      notes: shiftReportData.value.notes,
      pettyCash,
      remainingCashSales,
      denominations: shiftReportData.value.denominations,
      enteredTotal: enteredTotal.value
    });

    // Ensure denominations object is properly formatted with all required keys
    const denominationsPayload = {};
    denominations.forEach(denom => {
      const value = shiftReportData.value.denominations[denom.value];
      // Ensure we send a number, not undefined
      denominationsPayload[denom.value] = value !== undefined && value !== null ? Number(value) : 0;
    });

    // Verify all denominations are present
    const validDenominations = [1000, 500, 200, 100, 50, 20, 10, 5, 1];
    validDenominations.forEach(denom => {
      if (!(denom in denominationsPayload)) {
        denominationsPayload[denom] = 0;
      }
    });

    console.log('Sending denominations payload:', JSON.stringify(denominationsPayload, null, 2));

    // Fetch report data from backend
    const response = await api.post('/reports/shift-report', {
      date: shiftReportData.value.date,
      staffName,
      notes: shiftReportData.value.notes,
      pettyCash,
      remainingCashSales,
      denominations: denominationsPayload
    });

    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to fetch shift report data');
    }

    const reportData = response.data.data;

    // Generate PDF on frontend
    const { generateShiftReport } = await import('@/utils/generateShiftReport');
    const doc = await generateShiftReport(reportData);

    // Save and download PDF
    doc.save(`shift-report-${shiftReportData.value.date}.pdf`);

    toast.success('Shift report PDF generated successfully');

    // Show variance alert if significant
    if (reportData.cashCount && Math.abs(reportData.cashCount.variance) > 100) {
      const varianceStatus = reportData.cashCount.varianceStatus;
      const varianceAmount = Math.abs(reportData.cashCount.variance);
      const message = varianceStatus === 'shortage'
        ? `⚠ Cash shortage detected: P${varianceAmount.toFixed(2)}. Manager has been notified.`
        : `⚠ Cash overage detected: P${varianceAmount.toFixed(2)}. Manager has been notified.`;
      toast.warning(message);
    }

    // Close modal after successful generation
    showShiftReportModal.value = false;

  } catch (error) {
    console.error('Error generating shift report PDF:', error);
    const errorMessage = error.response?.data?.message || error.message || 'Failed to generate shift report PDF';
    toast.error(errorMessage);
  } finally {
    shiftReportLoading.value = false;
  }
};

// Preview modals
const openExpensePreviewModal = async (expenseId) => {
  try {
    // Fetch the expense details using the main expenses endpoint with a filter
    const response = await api.get('/expenses', {
      params: {
        id: expenseId
      }
    });

    if (response.data?.data && response.data.data.length > 0) {
      selectedExpense.value = response.data.data[0];
      showExpensePreviewModal.value = true;
    } else {
      toast.error('Expense details not found');
    }
  } catch (error) {
    console.error('Error fetching expense details:', error);
    toast.error('Failed to load expense details');
  }
};

// Add these helper functions after the approval functions
const truncateText = (text, length = 15) => {
  if (!text) return '';
  return text.length > length ? text.substring(0, length) + '...' : text;
};

// Function to open order preview modal
const openOrderPreviewModal = (orderId) => {
  selectedOrderId.value = orderId;
  showOrderPreviewModal.value = true;
};

// Function to open payment preview modal
const openPaymentPreviewModal = (paymentId) => {
  selectedPaymentId.value = paymentId;
  showPaymentPreviewModal.value = true;
};

// View Order from payment follow-ups modal
const viewFollowupOrder = (orderId) => {
  showPaymentFollowUpsModal.value = false;
  openOrderPreviewModal(orderId);
};

// Navigate to record payment page with client details
const navigateToRecordPayment = (followup) => {
  showPaymentFollowUpsModal.value = false;
  router.push({
    name: 'payments',
    query: {
      client_id: followup.clientId,
      order_id: followup.id,
      action: 'record_payment'
    }
  });
};

// Open payment follow-ups modal
const openPaymentFollowUpsModal = (data) => {
  showPaymentFollowUpsModal.value = true;
};

// Open client payment status modal
const openClientPaymentStatusModal = () => {
  showClientPaymentStatusModal.value = true;
};

// View client details from client payment status modal
const viewClientDetails = async (client) => {
  if (!client?.id) {
    console.warn('[SalesDashboard] viewClientDetails called without a valid client id', client);
    return;
  }

  let detailedClient = { ...client };

  try {
    const response = await api.get(`/clients/${client.id}`);
    const clientData = response?.data?.data || response?.data || {};

    detailedClient = {
      ...detailedClient,
      ...clientData,
      // Ensure we preserve the metrics coming from the payment status modal
      pendingOrders: detailedClient.pendingOrders,
      outstanding: detailedClient.outstanding
    };
  } catch (error) {
    console.error('[SalesDashboard] Failed to load client details for orders modal:', error);
  }

  // Fallbacks to ensure consistent fields for the modal
  detailedClient = {
    ...detailedClient,
    display_name: detailedClient.display_name || detailedClient.name,
    company_name: detailedClient.company_name || detailedClient.company || null,
    email: detailedClient.email || detailedClient.contact_email || null,
    phone: detailedClient.phone || detailedClient.contact_number || detailedClient.phone_number || null
  };

  selectedClientForOrders.value = detailedClient;
  showClientOrdersModal.value = true;
};

// Handle view order from client orders modal
const viewOrderFromClientModal = (orderId) => {
  // Keep the Client Orders Modal open so user can view multiple orders
  openOrderPreviewModal(orderId);
};

// Add loading state management functions
const startLoading = () => {
  loading.value = true;
  error.value = null;
};

const stopLoading = () => {
  loading.value = false;
};

const handleError = (err) => {
  error.value = err?.message || 'An error occurred';
  loading.value = false;
};


// Floating action button methods - dispatch events to GlobalFabModals
const createOrder = () => {
  showFloatingTools.value = false;
  window.dispatchEvent(new CustomEvent('fab-action', { detail: { action: 'create-order' } }));
};

const createClient = () => {
  showFloatingTools.value = false;
  window.dispatchEvent(new CustomEvent('fab-action', { detail: { action: 'create-client' } }));
};

const addPayment = () => {
  showFloatingTools.value = false;
  window.dispatchEvent(new CustomEvent('fab-action', { detail: { action: 'add-payment' } }));
};

const createExpense = () => {
  showFloatingTools.value = false;
  window.dispatchEvent(new CustomEvent('fab-action', { detail: { action: 'create-expense' } }));
};

const createQuotation = () => {
  showFloatingTools.value = false;
  window.dispatchEvent(new CustomEvent('fab-action', { detail: { action: 'create-quotation' } }));
};

const generateReport = () => {
  showFloatingTools.value = false;
  navigateToShiftReport();
};

const createTools = () => {
  showFloatingTools.value = false;
  window.dispatchEvent(new CustomEvent('fab-action', { detail: { action: 'tools' } }));
};

</script>

<style scoped>
/* Glassmorphism Utilities - REMOVED for performance */
/* .glass-panel {
  @apply bg-white/80 dark:bg-dark-800/80 backdrop-blur-md border border-white/20 dark:border-white/10;
} */

/* Modern Animations */
@keyframes fade-in-up {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in-up {
  animation: fade-in-up 0.4s ease-out forwards;
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

.animate-shimmer {
  animation: shimmer 1.5s infinite;
  background-size: 200% 100%;
  opacity: 0.1;
}

@keyframes bounce-small {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-3px);
  }
}

.animate-bounce-small {
  animation: bounce-small 2s infinite;
}

/* Custom Scrollbar */
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: rgba(156, 163, 175, 0.5);
  border-radius: 20px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background-color: rgba(107, 114, 128, 0.8);
}

/* Modal Transitions */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

/* Card Hover Effects - REMOVED for performance */
/* .glass-panel:hover {
  transform: translateY(-2px);
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
} */

/* Fix for modal z-index */
.fixed.inset-0.z-10 {
  z-index: 1000 !important;
}

.fixed.inset-0.bg-gray-500.bg-opacity-75 {
  z-index: 999 !important;
}

/* Mobile Optimizations */
@media (max-width: 640px) {
  /* Ensure floating action buttons don't overlap content */
  main {
    padding-bottom: 6rem !important;
  }

  /* Improve touch targets */
  button {
    min-height: 44px;
  }

  /* Better spacing for mobile cards */
  .space-y-3 > * + * {
    margin-top: 0.75rem;
  }

  /* Improve modal padding on mobile */
  .modal-content {
    margin: 1rem;
  }

  /* Better scrolling for horizontal action buttons */
  .overflow-x-auto {
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    -ms-overflow-style: none;
  }

  .overflow-x-auto::-webkit-scrollbar {
    display: none;
  }
}

/* Smooth Transitions for All Interactive Elements */
* {
  transition-property: color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;
}
</style>
